'use client';

import { CustomEventName, GTMConstants, GTMEventKey, sendGTM } from '@fe-cnyes/fe-common-gtm';
import ReactSlick from '@fe-common-ui/Slick';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import NewsTypeBadge from '@fe-news/components/molecules/NewsTypeBadge/NewsTypeBadge';
import { styled } from '@linaria/react';
import Image from 'next/image';
import Link from 'next/link';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import DotsWrapper from './DotsWrapper';

const StyledSlider = styled.div`
  position: relative;
  overflow-x: hidden;
  background-color: #fff;
  padding-bottom: 27px;
  margin-top: 24px;

  img {
    border-radius: 10px;
  }
`;

const View = styled.div`
  .slide {
    padding: 0 8px;
  }

  .image-container {
    display: block;
    height: 200px;
    position: relative;

    img {
      object-fit: cover;
    }

    @media only screen and (min-width: ${SCREEN_SIZE.MOBILE}px) {
      height: 400px;
    }
  }
`;

const Mask = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: linear-gradient(to bottom, rgba(49, 62, 79, 0), rgba(22, 29, 39, 0.6));
  border-radius: 10px;
`;

const Title = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  right: 12px;
  width: calc(100% - 24px);
  text-shadow: 0 0 6px rgba(0, 0, 0, 0.5);
  font-size: 20px;
  font-weight: 500;
  line-height: 30px;
  letter-spacing: 0.8px;
  color: #fff;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ArrowTouch = styled.button`
  display: block;
  position: absolute;
  top: 0;
  width: 10%;
  height: 100%;
  background-color: transparent;
  z-index: 2;
`;

interface NavigatorCarouselInterface {
  news: News[];
}

const onClick = (id: number, title: string) => {
  sendGTM({
    [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_NEWS,
    [GTMEventKey.SECTION]: `${GTMConstants.SECTION_PLACEHOLDER}_精選新聞`,
    [GTMEventKey.CLICK_ITEM]: `${id}_${title}`
  });
};

const NavigatorCarousel: FC<NavigatorCarouselInterface> = ({ news }) => {
  const [scrollIndex, setScrollIndex] = useState(0);
  const sliderReference = useRef<null | {
    slickNext: () => void;
    slickPrev: () => void;
  }>(null);
  const isEmpty = news?.length === 0;

  const settings = useMemo(
    () => ({
      dots: false,
      className: 'center',
      centerMode: true,
      infinite: true,
      centerPadding: '24px',
      slidesToShow: 1,
      speed: 500,
      autoplay: false,
      autoplaySpeed: 4000,
      beforeChange: (_: number, newIndex: number) => {
        setScrollIndex(newIndex);
      }
    }),
    []
  );

  const onForceSlide = useCallback(
    (back = false) =>
      () => {
        if (sliderReference.current) {
          if (back) {
            sliderReference.current.slickNext();
          } else {
            sliderReference.current.slickPrev();
          }
        }
      },
    []
  );

  if (isEmpty) return null;

  return (
    <StyledSlider>
      <ArrowTouch style={{ left: 0 }} onClick={onForceSlide()} />
      <ReactSlick ref={sliderReference} {...settings}>
        {news?.map(({ coverSrc, title, newsId, is24h, keyword }, index) => {
          const href = `/news/id/${newsId}`;
          const defaultImageSource = `/assets/images/news_default_images/${newsId % 10}.webp`;
          const source = coverSrc?.l?.src || defaultImageSource;

          return (
            <View key={`${title}-${newsId}`}>
              <div className='slide'>
                <Link className='image-container' prefetch={false} href={href} onClick={() => onClick(newsId, title)}>
                  <Image priority={index === 0} alt='news-cover' src={source} fill />
                  <Mask />
                  <Title>
                    <NewsTypeBadge is24h={Boolean(is24h)} isMobile keyword={keyword} />
                    {title}
                  </Title>
                </Link>
              </div>
            </View>
          );
        })}
      </ReactSlick>
      <DotsWrapper newsNumber={news?.length ?? 0} scrollIndex={scrollIndex} />
      <ArrowTouch style={{ right: 0 }} onClick={onForceSlide(true)} />
    </StyledSlider>
  );
};

export default NavigatorCarousel;
